// Package imports:
import React, { useState } from 'react';
import { SwiperSlide } from 'swiper/react';
// Component imports:
import Button from '../../ui-elements/Button/Button';
import TextImageBlock from '../TextImageBlock/TextImageBlock';
import PriceCard from '../PriceCard/PriceCard';
import Slider from '../Slider/Slider';
import FullScreenImage from '../../ui-elements/FullScreenImage/FullScreenImage';
import HeadSection from '../../ui-elements/HeadSection/HeadSection';
import DueDiligenceForm from '../ContactForms/DueDiligenceForm/DueDiligenceForm';
import Link from '../../ui-elements/Link/Link';
import BulletList from '../../ui-elements/Lists/BulletList';
// Service imports:
import { getSubscriptionPrice } from '../../services/utils';
import Tooltip from '../../ui-elements/Tooltip/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

const AREIDANLEIKAKANNANIR_IMAGES_URLS = [
    "https://cdn.livemarketdata.com/KeldanImages/KYCkerfi_1.png",
    "https://cdn.livemarketdata.com/KeldanImages/KYCkerfi_2.png",
    "https://cdn.livemarketdata.com/KeldanImages/KYCkerfi_3.png"
];

const BULLET_POINTS: JSX.Element[] = [
    <span>Áreiðanleikakannanir á <strong>lögaðilum</strong> og <strong>einstaklingum</strong>.</span>,
    <span>Áreiðanleikakannanir á <strong>erlendum aðilum</strong>.</span>,
    <span><strong>Gildandi skráning með raunverulegum eigendum</strong>, sótt sjálfkrafa frá Hlutafélagaskrá.</span>,
    // this is not ready yet
    // <span>Sjálfvirk athugun <strong>orðsporsáhættu</strong> (fjölmiðlar og dómar).</span>,
    <span>Sjálfvirk leit á íslenskum <strong>PEP lista Keldunnar</strong>.</span>,
    <span>Sjálfvirk leit á móti <strong>alþjóðlegum válistum</strong>.</span>,
    <span><strong>Ríkisfang</strong> einstaklinga keyrt á móti áhættu- og ósamvinnuþýðum ríkjum.</span>,
    <span><strong>Spurningalisti</strong> sendur út og svör við spurningalista móttekin.</span>,
    <span><strong>Rafræn innskráning</strong> viðskiptavina (framvísun persónuskilríkja).</span>,
    <span>Skráning <strong>áhættumats</strong> handvirkt eða sjálfvirkt.</span>,
    <span>Skjöl hengd við hverja áreiðanleikakönnun <strong>(aukin áreiðanleikakönnun)</strong>.</span>,
    <span><strong>Sjálfvirk áminning</strong> þegar ákveðinn tími er liðinn frá síðustu könnun.</span>,
    <span><strong>Viðvarandi eftirlit</strong> með stöðu viðskiptavina á móti innlendum og erlendum válistum.</span>,
    <span>Góð <strong>heildstæð yfirsýn</strong> á viðskiptavinum.</span>
];

const Areidanleikakannanir: React.FC = () => {
    const [fullScreenImageUrl, setFullScreenImageUrl] = useState<string | null>(null);
    const handleListItems = () => {
        return (
            <BulletList items={BULLET_POINTS} />
        )
    }
    return (
        <div className='main KCL_areidanleikakannanir-page'>
            {fullScreenImageUrl && (
                <FullScreenImage
                    imgUrl={fullScreenImageUrl}
                    imgAlt="Fullscreen Áreiðanleikakönnun screenshot"
                    onExit={() => setFullScreenImageUrl(null)}
                />
            )}
            <div className="main__inner-fluid grid-items">
                <HeadSection
                    title="Áreiðanleikakönnun - KYC kerfi fyrir tilkynningarskylda aðila"
                    summary={
                        <p>
                            Hvíla á þér skyldur um aðgerðir gegn peningaþvætti og fjármögnun hryðjuverka? <br />
                            Keldan býður upp á öflugar lausnir sem aðstoða tilkynningarskylda aðila við að framkvæma áreiðanleikakönnun og viðhafa eftirlit með viðskiptavinum á einfaldan og öruggan hátt.
                        </p>
                    }
                    actions={
                        <>
                            <Button
                                size="lg"
                                buttonType='secondary'
                                anchorProps={{
                                    href: '/Areidanleikakannanir/Yfirlit',
                                }}
                            >
                                KYC kerfið
                            </Button>

                            <Button
                                size="lg"
                                buttonType='primary'
                                anchorProps={{
                                    href: '#kynning',
                                }}
                            >
                                Panta kynningu
                            </Button>
                            <Link icon='angleRight' href='https://kyc.keldan.is/'
                            >
                                Svara áreiðanleikakönnun
                            </Link>
                        </>
                    }
                    textAlign='center'
                />
                <div className="section__body">
                    <div className="shell">
                        <TextImageBlock
                            imgSide="right"
                            imgSrc="https://cdn.livemarketdata.com/KeldanImages/Areidanleikakonnun_KYC.png"
                        >
                            <h2>Áreiðanleiki og yfirsýn</h2>
                            <div className='text-image-block__entry'>
                                <p className='paragraph'>KYC kerfið („Know your customer“) er ætlað til utanumhalds og framkvæmdar áreiðanleikakannana og stenst þau lög sem tilkynningarskyldir aðilar falla undir um aðgerðir gegn peningaþvætti (AML) og fjármögnun hryðjuverka (CTF).</p>
                                <p className='paragraph'>Kerfið gerir notendum kleift að framkvæma áreiðanleikakannanir á einfaldan hátt og öðlast góða yfirsýn á viðskiptavinum, lögaðilum og einstaklingum.</p>
                            </div>
                        </TextImageBlock>
                        <TextImageBlock
                            imgSide="left"
                            imgSrc='https://cdn.livemarketdata.com/KeldanImages/Areidanleikakonnun_example_mynd.png'
                        >
                            <h2>Innifalið í kerfinu</h2>
                            <div className="text-image-block__entry">
                                {handleListItems()}
                            </div>
                        </TextImageBlock>
                        <div className="text-image-block-list">
                            <TextImageBlock
                                imgSide="right"
                            >
                                <h3>AML vöktun</h3>
                                <div className="text-image-block__entry">
                                    <p className='paragraph'>
                                        AML vaktin er þjónusta sem gerir tilkynningarskyldum aðilum kleift að framkvæma reglubundna athugun á sínum viðskiptavinum með sjálfvirkum hætti. Vöktunin fer fram í umboði þjónustukaupa sem er ábyrgðaraðili í skilningi laga nr. 90/2018 um persónuvernd, og Keldan er vinnsluaðili við þá þjónustu.
                                    </p>
                                    <p className='paragraph'>
                                        Ef þú hefur spurningar, vinsamlegast <Link linkSize='18' url='/Hafa-samband/AML-Vaktin'>hafðu samband við Kelduna</Link>.
                                    </p>
                                    <Button
                                        buttonType='secondary'
                                        size='lg'
                                        anchorProps={{ href: "/Valistar/Leit#Pep-vaktin", style: { marginTop: '20px' } }}>
                                        AML vaktin
                                    </Button>
                                </div>
                            </TextImageBlock>
                            <TextImageBlock>
                                <h3>PEP listi - stjórnmálaleg tengsl</h3>
                                <div className="text-image-block__entry">
                                    <p className='paragraph'>
                                        Keldan heldur úti íslenskum PEP lista sem er aðgengilegur tilkynningarskyldum aðilum. Á listanum eru einstaklingar sem eru eða hafa verið háttsettir í opinberri þjónustu ásamt nánustu fjölskyldu og samstarfsmönnum á síðustu 12 mánuðum.
                                    </p>
                                    <p className='paragraph'>
                                        PEP listinn er einnig aðgengilegur sem vefþjónusta sé þess óskað og skal þá <Link linkSize='18' url='/Hafa-samband/Pep'>hafa samband við Kelduna.</Link>
                                    </p>
                                    <Button
                                        buttonType='secondary'
                                        size='lg'
                                        anchorProps={{ href: "/Valistar/Leit", style: { marginTop: '20px' } }}>
                                        Uppfletting í PEP lista
                                    </Button>
                                </div>
                            </TextImageBlock>
                        </div>
                    </div>
                </div>
                <div className="section section--gray section--contact" id="kynning">
                    <div className="shell">
                        <div className="section__inner">
                            <div className="section__head">
                                <h2>Fá kynningu á KYC kerfinu</h2>
                                <p className='summary-paragraph' style={{ maxWidth: '636px' }}>
                                    Hafðu samband ef þú vilt fá aðgang að Keldan KYC eða til að panta kynningu á kerfinu.
                                </p>
                                <DueDiligenceForm />
                            </div>
                            <div className="section__body">
                                <PriceCard
                                    variant="overview"
                                    type="Verðskrá KYC kerfisins "
                                >
                                    <div className="price-card__details">
                                        <dl>
                                            <dt className='paragraph--bold'>Keldan áskrift</dt>
                                            <dd className='paragraph--bold'>{getSubscriptionPrice(true)}</dd>
                                        </dl>
                                        <p className='paragraph-small--italic' style={{ marginBottom: '15px' }}>
                                            Mánaðargjald
                                        </p>
                                        <dl>
                                            <dt className='paragraph--bold'>Aðgangur að KYC kerfinu</dt>
                                            <dd className='paragraph--bold'>1.990 kr.</dd>
                                        </dl>
                                        <p className='paragraph-small--italic' style={{ marginBottom: '15px' }}>
                                            Mánaðargjald
                                        </p>
                                        <div className='content-wrapper'>
                                            <dl style={{ marginBottom: '15px' }}>
                                                <dt>Áreiðanleikakönnun á lögaðilum</dt>
                                                <dd>1.490 kr.</dd>
                                            </dl>
                                            <dl style={{ marginBottom: '15px' }}>
                                                <dt>Áreiðanleikakönnun á einstaklingum</dt>
                                                <dd>790 kr.</dd>
                                            </dl>
                                        </div>
                                        <dl>
                                            <dt className='paragraph--bold'>Vöktun <Tooltip tooltipClassName='kyc-pricecard-tooltip' noUnderline text={<FontAwesomeIcon icon={faInfoCircle} />} tooltip={<>Vöktun einstaklings á móti PEP (mánaðargjald)  24 kr.<br />Vöktun einstaklings á móti alþjóðlegum válistum (ársfjórðungsgjald)  195 kr.</>} /></dt>
                                            <dd>24 kr.</dd>
                                        </dl>
                                        <p className='paragraph-small--italic' style={{ marginBottom: '15px' }}>
                                            Mánaðargjald
                                        </p>
                                        <p className='paragraph-small--italic'>Athugið, verðin eru án VSK.</p>
                                    </div>
                                </PriceCard>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="section">
                    <div className="section__head">
                        <div className="shell">
                            <h2 id="Vaktarinn_Screenshots">Skjáskot úr KYC kerfinu</h2>
                        </div>
                    </div>
                    <div className="section__body">
                        <div className="shell">
                            <Slider
                                className="slider-screenshots"
                                perView={3}
                                centerSlides={false}
                                loop={true}
                                breakpointOptions={{
                                    320: {
                                        slidesPerView: 1
                                    },
                                    768: {
                                        slidesPerView: 3
                                    }
                                }}
                                onInit={() => { }}
                                onSlideChange={() => { }}
                            >
                                {AREIDANLEIKAKANNANIR_IMAGES_URLS.map((url) => (
                                    <SwiperSlide
                                        key={url}
                                        onClick={() => setFullScreenImageUrl(url)}
                                    >
                                        <img
                                            className="slide-image"
                                            src={url}
                                            alt="Áreiðanleikakannanir kerfi skjáskot"
                                            width="1406"
                                            height="811"
                                        />
                                    </SwiperSlide>
                                ))}
                            </Slider>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Areidanleikakannanir;